import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Share2,
  X as CloseIcon,
  MessageCircle,
  Send,
  Mail,
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  Check,
} from "lucide-react";

const SHARE_TITLE = "Sunlit Happiness · Happiness Coaching with Dr. Vrinda Jayaram";
const SHARE_TEXT =
  "I found this warm space — happiness coaching, women's listening sessions, and a free 15-minute clarity call. Thought you might like it. 🌻";

export default function ShareButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Always use the live page's URL (so deploy/preview both behave correctly)
  const url = typeof window !== "undefined" ? window.location.origin + "/" : "";
  const enc = encodeURIComponent;

  const targets = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${enc(SHARE_TEXT + " " + url)}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      icon: Send,
      href: `https://t.me/share/url?url=${enc(url)}&text=${enc(SHARE_TEXT)}`,
    },
    {
      id: "twitter",
      label: "X (Twitter)",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${enc(SHARE_TEXT)}&url=${enc(url)}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    },
    {
      id: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${enc(SHARE_TITLE)}&body=${enc(SHARE_TEXT + "\n\n" + url)}`,
    },
  ];

  const handleClick = async () => {
    // Try the native share sheet first (mobile gets WhatsApp/Insta/Messages there)
    if (navigator.share) {
      try {
        await navigator.share({
          title: SHARE_TITLE,
          text: SHARE_TEXT,
          url,
        });
        return;
      } catch {
        // user cancelled — fall through to popover
      }
    }
    setOpen(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied. Paste it anywhere.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy. Please copy from the address bar.");
    }
  };

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open]);

  return (
    <>
      {/* Floating share button */}
      <button
        onClick={handleClick}
        aria-label="Share Sunlit Happiness"
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-sunlit-gold hover:bg-sunlit-gold-hover text-sunlit-ink px-5 py-3 text-sm font-medium shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
        data-testid="share-floating-button"
      >
        <Share2 className="h-4 w-4" />
        <span className="hidden sm:inline">Share</span>
      </button>

      {/* Desktop fallback / cancelled-mobile popover */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-sunlit-ink/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="share-modal"
        >
          <div
            className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-sunlit-ivory border border-stone-200 p-6 sm:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-sunlit-mute mb-2">Share Sunlit Happiness</p>
                <h3 className="font-serif-display text-2xl text-sunlit-ink leading-tight">
                  Pass on a little <span className="italic text-sunlit-gold">sunshine</span>.
                </h3>
                <p className="mt-2 text-sm text-sunlit-mute">
                  Someone you know may quietly need this.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="h-9 w-9 rounded-full hover:bg-sunlit-cream flex items-center justify-center"
                data-testid="share-modal-close"
              >
                <CloseIcon className="h-4 w-4 text-sunlit-mute" />
              </button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {targets.map((t) => {
                const Icon = t.icon;
                return (
                  <a
                    key={t.id}
                    href={t.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 rounded-2xl border border-stone-200 bg-sunlit-cream hover:bg-sunlit-gold/15 hover:border-sunlit-gold/40 py-4 transition-all"
                    data-testid={`share-target-${t.id}`}
                    onClick={() => setOpen(false)}
                  >
                    <Icon className="h-5 w-5 text-sunlit-ink" />
                    <span className="text-xs font-medium text-sunlit-ink">{t.label}</span>
                  </a>
                );
              })}
            </div>

            <button
              onClick={copyLink}
              className="mt-4 w-full inline-flex items-center justify-between gap-2 rounded-2xl border border-stone-200 bg-sunlit-cream hover:bg-sunlit-gold/15 hover:border-sunlit-gold/40 px-4 py-3 text-sm font-medium text-sunlit-ink transition-all"
              data-testid="share-copy-link"
            >
              <span className="inline-flex items-center gap-2">
                {copied ? <Check className="h-4 w-4 text-sunlit-gold" /> : <LinkIcon className="h-4 w-4" />}
                {copied ? "Copied" : "Copy link"}
              </span>
              <span className="text-xs text-sunlit-mute truncate max-w-[55%]">{url.replace(/^https?:\/\//, "")}</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
